export interface HelpItem {
    id: string;
    title: string;
    category: 'tool' | 'setting' | 'concept';
    subcategory?: string;
    description: string;
    usage?: string;
    examples?: string[];
    relatedItems?: string[];
    keywords: string[];
}

export const helpData: HelpItem[] = [
    // TOOLBAR TOOLS
    {
        id: 'select-tool',
        title: 'Select Tool',
        category: 'tool',
        subcategory: 'navigation',
        description:
            'The default tool for selecting and moving screens and elements on the canvas. Use this to navigate between screens and select elements for editing.',
        usage: 'Click on screens or elements to select them. Drag screens to reposition them on the canvas.',
        examples: [
            'Click a screen to view its properties',
            'Drag a screen to organize your experience layout',
            'Click an element to edit its properties',
        ],
        keywords: [
            'select',
            'move',
            'navigate',
            'click',
            'drag',
            'cursor',
            'pointer',
        ],
    },
    {
        id: 'pan-tool',
        title: 'Pan Tool',
        category: 'tool',
        subcategory: 'navigation',
        description:
            'Allows you to pan around the canvas without accidentally selecting elements. Useful for navigating large experiences.',
        usage: 'Click and drag anywhere on the canvas to move your view around.',
        examples: [
            'Navigate to different areas of your experience',
            'Move around without selecting elements',
        ],
        keywords: ['pan', 'move', 'navigate', 'canvas', 'view', 'scroll'],
    },
    {
        id: 'zoom-controls',
        title: 'Zoom Controls',
        category: 'tool',
        subcategory: 'navigation',
        description:
            'Control the zoom level of the canvas to get a better view of your experience layout.',
        usage: 'Use zoom in/out buttons or the zoom percentage dropdown to adjust your view.',
        examples: [
            'Zoom out to see the entire experience flow',
            'Zoom in to work on detailed element positioning',
        ],
        keywords: ['zoom', 'scale', 'view', 'magnify', 'fit', 'percentage'],
    },
    {
        id: 'device-selector',
        title: 'Device Selector',
        category: 'tool',
        subcategory: 'preview',
        description:
            'Choose different device types and orientations to preview how your experience will look on various screens.',
        usage: 'Select from iPhone, iPad, or other device types. Toggle between portrait and landscape orientations.',
        examples: [
            'Preview on iPhone 15 Pro in portrait mode',
            'Test iPad layout in landscape orientation',
        ],
        keywords: [
            'device',
            'preview',
            'iPhone',
            'iPad',
            'portrait',
            'landscape',
            'orientation',
            'screen size',
        ],
    },
    {
        id: 'connections-toggle',
        title: 'Navigation Connections',
        category: 'tool',
        subcategory: 'visualization',
        description:
            'Toggle the display of navigation connections between screens to visualize the user flow.',
        usage: 'Click to show/hide lines connecting screens based on navigation actions.',
        examples: [
            'See how button clicks connect screens',
            'Visualize automatic navigation after capture',
        ],
        keywords: [
            'connections',
            'navigation',
            'flow',
            'arrows',
            'links',
            'user journey',
        ],
    },
    {
        id: 'add-screen',
        title: 'Add Screen',
        category: 'tool',
        subcategory: 'creation',
        description:
            'Create new screens for your experience. Each screen represents a step in the user journey.',
        usage: 'Click to add a new blank screen to your experience.',
        examples: [
            'Add a capture screen for taking photos',
            'Create a review screen for user confirmation',
        ],
        keywords: ['add', 'create', 'new', 'screen', 'stage', 'step'],
    },
    {
        id: 'screen-templates',
        title: 'Screen Templates',
        category: 'tool',
        subcategory: 'creation',
        description:
            'Access pre-built screen templates for common use cases like attract, capture, review, and share screens.',
        usage: 'Browse templates by category and click to add them to your experience.',
        examples: [
            'Use an attract template to welcome users',
            'Add a capture template for photo taking',
        ],
        keywords: [
            'templates',
            'presets',
            'attract',
            'capture',
            'review',
            'share',
            'processing',
        ],
    },
    {
        id: 'element-palette',
        title: 'Element Palette',
        category: 'tool',
        subcategory: 'creation',
        description:
            'Access UI elements that can be added to screens, such as buttons, text, images, and interactive components.',
        usage: 'Drag elements from the palette onto screens to build your interface.',
        examples: [
            'Add a button for user interaction',
            'Place text elements for instructions',
        ],
        keywords: [
            'elements',
            'palette',
            'UI',
            'components',
            'drag',
            'drop',
            'button',
            'text',
            'image',
        ],
    },
    {
        id: 'replicate-tool',
        title: 'Replicate Tool',
        category: 'tool',
        subcategory: 'efficiency',
        description:
            'Copy property values from one screen or element to others. Useful for maintaining consistency across your experience.',
        usage: 'Select source properties, then choose target screens/elements to apply the same values.',
        examples: [
            'Copy button colors across all screens',
            'Apply consistent font settings to text elements',
        ],
        keywords: [
            'replicate',
            'copy',
            'paste',
            'consistency',
            'bulk',
            'properties',
            'values',
        ],
    },
    {
        id: 'legend',
        title: 'Navigation Legend',
        category: 'tool',
        subcategory: 'reference',
        description:
            'View a legend explaining the different types of navigation connections and their visual representations.',
        usage: 'Click to open a reference guide for understanding connection types.',
        examples: [
            'Learn what different arrow styles mean',
            'Understand automatic vs manual navigation',
        ],
        keywords: [
            'legend',
            'reference',
            'guide',
            'connections',
            'arrows',
            'navigation types',
        ],
    },

    // SCREEN SETTINGS
    {
        id: 'screen-id',
        title: 'Screen ID',
        category: 'setting',
        subcategory: 'screen-basic',
        description:
            'Unique identifier for the screen. Used for navigation and referencing in the experience flow.',
        usage: "Enter a descriptive name that helps identify the screen's purpose.",
        examples: ['welcome-screen', 'photo-capture', 'review-photos'],
        keywords: ['id', 'identifier', 'name', 'reference', 'unique'],
    },
    {
        id: 'stage-type',
        title: 'Stage Type',
        category: 'setting',
        subcategory: 'screen-basic',
        description:
            'Defines the primary function of the screen in the user experience flow.',
        usage: "Choose from attract, capture, review, share, processing, or query based on the screen's purpose.",
        examples: [
            'Attract: Welcome users and start the experience',
            'Capture: Take photos or videos',
            'Review: Let users see and approve their media',
        ],
        keywords: [
            'stage',
            'type',
            'attract',
            'capture',
            'review',
            'share',
            'processing',
            'query',
        ],
    },
    {
        id: 'background-color',
        title: 'Background Color',
        category: 'setting',
        subcategory: 'screen-appearance',
        description:
            'Sets the background color of the screen. Use colors that match your brand and provide good contrast.',
        usage: 'Enter a hex color code or use the color picker to select a background color.',
        examples: [
            '#ffffff for white background',
            '#000000 for black background',
            '#007AFF for brand blue',
        ],
        keywords: [
            'background',
            'color',
            'hex',
            'appearance',
            'brand',
            'contrast',
        ],
    },
    {
        id: 'background-media',
        title: 'Background Media',
        category: 'setting',
        subcategory: 'screen-appearance',
        description:
            'Add a background image or video to the screen. Media will be scaled to fit the screen dimensions.',
        usage: 'Enter a URL to an image or video file, or use the asset picker to select from your library.',
        examples: [
            'Brand logo as background image',
            'Animated background video for attract screen',
        ],
        keywords: ['background', 'media', 'image', 'video', 'asset', 'URL'],
    },
    {
        id: 'capture-mode',
        title: 'Capture Mode',
        category: 'setting',
        subcategory: 'capture',
        description:
            'Determines how photos or videos are captured on this screen.',
        usage: 'Select the appropriate mode based on your hardware setup and desired user experience.',
        examples: [
            'Photo: Single photo capture',
            'Sequence: Multiple photos in succession',
            'GIF: Animated GIF creation',
        ],
        keywords: [
            'capture',
            'mode',
            'photo',
            'video',
            'sequence',
            'gif',
            'camera',
        ],
    },
    {
        id: 'flow-id',
        title: 'Processing Flow',
        category: 'setting',
        subcategory: 'processing',
        description:
            'Selects which processing flow to apply to captured media. Processing flows define how images are edited, combined, or enhanced.',
        usage: 'Choose from available processing flows or create a new one to define how media should be processed.',
        examples: [
            'Apply filters and overlays',
            'Create photo strips or collages',
            'Add branded frames or text',
        ],
        keywords: [
            'processing',
            'flow',
            'filters',
            'overlays',
            'editing',
            'enhancement',
        ],
    },
    {
        id: 'post-capture-destination',
        title: 'Post Capture Destination',
        category: 'setting',
        subcategory: 'screen-navigation',
        description:
            'Defines which screen to navigate to after media capture is complete.',
        usage: 'Select the next screen in your experience flow, typically a review or processing screen.',
        examples: [
            'Navigate to review screen after photo capture',
            'Go to processing screen for automatic editing',
        ],
        keywords: [
            'navigation',
            'destination',
            'post-capture',
            'next',
            'flow',
            'redirect',
        ],
    },
    {
        id: 'reset-after-timeout',
        title: 'Reset After Timeout',
        category: 'setting',
        subcategory: 'screen-navigation',
        description:
            'Automatically resets the experience to the first screen after a specified period of inactivity. Useful for public installations to ensure the experience returns to its starting state.',
        usage: 'Enter the number of seconds to wait before resetting. Set to null to disable automatic reset.',
        examples: [
            '30 seconds for quick reset in busy locations',
            '60 seconds for more patient users',
            'null to disable automatic reset',
        ],
        keywords: [
            'reset',
            'timeout',
            'inactivity',
            'automatic',
            'restart',
            'first screen',
            'public',
            'installation',
        ],
    },
    {
        id: 'countdown',
        title: 'Countdown Timer',
        category: 'setting',
        subcategory: 'capture',
        description:
            'Sets the countdown duration before capture begins. Gives users time to prepare for the photo.',
        usage: 'Enter the number of seconds for the countdown (typically 3-5 seconds).',
        examples: [
            '3 seconds for quick captures',
            '5 seconds for group photos',
        ],
        keywords: ['countdown', 'timer', 'seconds', 'preparation', 'delay'],
    },
    {
        id: 'photo-count',
        title: 'Photo Count',
        category: 'setting',
        subcategory: 'capture',
        description:
            'Number of photos to capture in sequence mode. Each photo will have its own countdown.',
        usage: 'Set the desired number of photos for the capture sequence.',
        examples: [
            '4 photos for a photo strip',
            '6 photos for a collage layout',
        ],
        keywords: [
            'photos',
            'count',
            'sequence',
            'multiple',
            'strip',
            'collage',
        ],
    },
    {
        id: 'prompt-text',
        title: 'Prompt Text',
        category: 'setting',
        subcategory: 'capture',
        description:
            'Instructions or prompts displayed to users during capture. Helps guide them through the experience.',
        usage: 'Enter helpful text that tells users what to do or how to pose.',
        examples: [
            'Say cheese!',
            'Strike a pose!',
            'Look at the camera and smile',
        ],
        keywords: [
            'prompt',
            'instructions',
            'text',
            'guidance',
            'pose',
            'message',
        ],
    },

    // ELEMENT SETTINGS
    {
        id: 'element-id',
        title: 'Element ID',
        category: 'setting',
        subcategory: 'element-basic',
        description:
            'Unique identifier for the UI element. Used for styling and interaction tracking.',
        usage: "Enter a descriptive name that identifies the element's purpose.",
        examples: ['start-button', 'welcome-text', 'logo-image'],
        keywords: ['element', 'id', 'identifier', 'name', 'unique'],
    },
    {
        id: 'button-action',
        title: 'Button Action',
        category: 'setting',
        subcategory: 'element-behavior',
        description:
            'Defines what happens when a button is clicked. Can navigate to other screens or trigger specific actions.',
        usage: 'Select the action type and configure the destination or behavior.',
        examples: [
            'Navigate to next screen',
            'Start capture process',
            'Share content',
        ],
        keywords: [
            'button',
            'action',
            'click',
            'navigate',
            'trigger',
            'behavior',
        ],
    },
    {
        id: 'button-destination',
        title: 'Button Destination',
        category: 'setting',
        subcategory: 'element-behavior',
        description:
            'Specifies which screen to navigate to when the button is clicked.',
        usage: 'Select the target screen from the dropdown list.',
        examples: [
            'Navigate to capture screen',
            'Go to review screen',
            'Return to start screen',
        ],
        keywords: ['destination', 'navigation', 'target', 'screen', 'redirect'],
    },
    {
        id: 'text-content',
        title: 'Text Content',
        category: 'setting',
        subcategory: 'element-content',
        description:
            'The actual text content displayed in text elements. Supports basic formatting and dynamic variables.',
        usage: 'Enter the text you want to display. Use variables like {userName} for dynamic content.',
        examples: [
            'Welcome to our photo booth!',
            'Hello {userName}!',
            'Tap to continue',
        ],
        keywords: [
            'text',
            'content',
            'message',
            'copy',
            'dynamic',
            'variables',
        ],
    },
    {
        id: 'font-family',
        title: 'Font Family',
        category: 'setting',
        subcategory: 'element-appearance',
        description:
            'Selects the typeface for text elements. Choose fonts that match your brand and are readable.',
        usage: 'Select from available system fonts or custom fonts uploaded to your asset library.',
        examples: [
            'Helvetica for clean, modern look',
            'Georgia for elegant serif style',
            'Custom brand font',
        ],
        keywords: ['font', 'typeface', 'family', 'text', 'typography', 'brand'],
    },
    {
        id: 'font-size',
        title: 'Font Size',
        category: 'setting',
        subcategory: 'element-appearance',
        description:
            'Controls the size of text elements. Measured in points (pt) for consistent scaling across devices.',
        usage: 'Enter a numeric value for the desired font size.',
        examples: [
            '24pt for headlines',
            '16pt for body text',
            '12pt for small details',
        ],
        keywords: ['font', 'size', 'text', 'points', 'scale', 'typography'],
    },
    {
        id: 'element-position',
        title: 'Element Position',
        category: 'setting',
        subcategory: 'element-layout',
        description:
            'Controls where the element appears on the screen using a 9-grid system (top/middle/bottom × left/center/right).',
        usage: 'Select the horizontal and vertical position from the grid options.',
        examples: [
            'Top-Center for headers',
            'Bottom-Right for action buttons',
            'Middle-Center for main content',
        ],
        keywords: [
            'position',
            'layout',
            'grid',
            'alignment',
            'placement',
            'location',
        ],
    },
    {
        id: 'element-layer',
        title: 'Element Layer',
        category: 'setting',
        subcategory: 'element-layout',
        description:
            'Controls the stacking order of elements. Higher layer numbers appear on top of lower ones.',
        usage: 'Enter a numeric value. Elements with higher numbers will appear above those with lower numbers.',
        examples: [
            'Layer 0 for background elements',
            'Layer 5 for buttons and interactive elements',
            'Layer 10 for overlays and modals',
        ],
        keywords: ['layer', 'z-index', 'stacking', 'order', 'depth', 'overlay'],
    },

    // CONCEPTS
    {
        id: 'user-flow',
        title: 'User Flow',
        category: 'concept',
        description:
            'The path users take through your experience, from start to finish. A well-designed flow guides users naturally through each step.',
        usage: 'Plan your screens in logical order and use navigation connections to create smooth transitions.',
        examples: [
            'Attract → Capture → Review → Share',
            'Welcome → Instructions → Photo → Processing → Results',
        ],
        keywords: [
            'flow',
            'journey',
            'path',
            'navigation',
            'sequence',
            'user experience',
        ],
    },
    {
        id: 'processing-flows',
        title: 'Processing Flows',
        category: 'concept',
        description:
            'Automated workflows that enhance, edit, or combine captured media. Can apply filters, overlays, layouts, and effects.',
        usage: 'Create processing flows in the visual editor by connecting processors in sequence.',
        examples: [
            'Apply vintage filter and add logo overlay',
            'Create 4-photo strip layout with borders',
            'Generate animated GIF from photo sequence',
        ],
        keywords: [
            'processing',
            'workflow',
            'filters',
            'effects',
            'automation',
            'enhancement',
        ],
    },
    {
        id: 'responsive-design',
        title: 'Responsive Design',
        category: 'concept',
        description:
            'Designing experiences that work well across different device sizes and orientations.',
        usage: 'Test your experience on different devices using the device selector and adjust layouts as needed.',
        examples: [
            'Portrait layout for phones',
            'Landscape layout for tablets',
            'Adaptive text sizes for different screens',
        ],
        keywords: [
            'responsive',
            'adaptive',
            'device',
            'orientation',
            'layout',
            'mobile',
            'tablet',
        ],
    },
    {
        id: 'asset-management',
        title: 'Asset Management',
        category: 'concept',
        description:
            'Organizing and using media files (images, videos, fonts) in your experiences. Assets are stored in your library for reuse.',
        usage: 'Upload assets to your library and reference them in background media, overlays, and other elements.',
        examples: [
            'Brand logos for consistent branding',
            'Background images for themed experiences',
            'Custom fonts for unique typography',
        ],
        keywords: [
            'assets',
            'media',
            'library',
            'images',
            'videos',
            'fonts',
            'branding',
        ],
    },
];

// Helper functions for searching and filtering help data
export function searchHelp(query: string): HelpItem[] {
    if (!query.trim()) return helpData;

    const searchTerm = query.toLowerCase();

    return helpData.filter(
        (item) =>
            item.title.toLowerCase().includes(searchTerm) ||
            item.description.toLowerCase().includes(searchTerm) ||
            item.keywords.some((keyword) =>
                keyword.toLowerCase().includes(searchTerm),
            ) ||
            (item.usage && item.usage.toLowerCase().includes(searchTerm)) ||
            (item.examples &&
                item.examples.some((example) =>
                    example.toLowerCase().includes(searchTerm),
                )),
    );
}

export function getHelpByCategory(category: HelpItem['category']): HelpItem[] {
    return helpData.filter((item) => item.category === category);
}

export function getHelpBySubcategory(subcategory: string): HelpItem[] {
    return helpData.filter((item) => item.subcategory === subcategory);
}

export function getHelpById(id: string): HelpItem | undefined {
    return helpData.find((item) => item.id === id);
}

export const helpCategories = {
    tool: 'Tools',
    setting: 'Settings',
    concept: 'Concepts',
};

export const helpSubcategories = {
    // Tools
    navigation: 'Navigation Tools',
    preview: 'Preview Tools',
    visualization: 'Visualization Tools',
    creation: 'Creation Tools',
    efficiency: 'Efficiency Tools',
    reference: 'Reference Tools',

    // Settings
    'screen-basic': 'Basic Screen Settings',
    'screen-appearance': 'Screen Appearance',
    capture: 'Capture Settings',
    processing: 'Processing Settings',
    'screen-navigation': 'Navigation Settings',
    'element-basic': 'Basic Element Settings',
    'element-behavior': 'Element Behavior',
    'element-content': 'Element Content',
    'element-appearance': 'Element Appearance',
    'element-layout': 'Element Layout',
};
